import * as Yup from "yup";
import { Utills } from "../../../../../shared/app-configs/utilit";
import type { ICertifiedOrganizationDetails } from "../../sc-models/certified-organization-details-dto";
import type { FacilityProcessDetails } from "../../sc-models/facility-product-dto";
import type { IProductCategoryInformation } from "../../sc-models/product-category-information-dto";
import type { IProductDetailInformation } from "../../sc-models/product-detail-information-dto";
import type { IRawMaterialDetails } from "../../sc-models/raw-material-details-dto";
import type {
  ScopeCertificateDetails,
  StandardDetails,
} from "../../sc-models/scope-certificate-details-dto";

type MaybeString = string | null | undefined;

export type ValidationErrorMap = Record<string, string>;

export interface RowValidationResult {
  fieldErrors: ValidationErrorMap;
  generalError?: string;
}

const trimString = (value: unknown) =>
  typeof value === "string" ? value.trim() : value;

export const isNaLike = (value?: MaybeString): boolean => {
  if (!value) return false;
  const lower = value.trim().toLowerCase();
  return (
    ["na", "n/a", "not available", "null", "none", "nil"].includes(lower) ||
    /^n\s*[\/\-\.]?\s*a\.?$/.test(lower)
  );
};

export const naOnlyValidator = (value?: MaybeString) => {
  if (!value) return true;
  if (!isNaLike(value)) return true;
  return value.trim().toLowerCase() === "na";
};

export const normalizeNaVariant = (value?: MaybeString) => {
  if (!value) return value;
  return isNaLike(value) ? "na" : value;
};

export const strictNormalizeNa = (value?: MaybeString) => {
  if (!value) return value;
  return value.trim().toLowerCase() === "na" ? "na" : value;
};

export const blockInvalidNaVariants = (value?: MaybeString) => {
  if (!value) return true;
  const lower = value.trim().toLowerCase();
  if (lower === "na") return true;
  if (
    ["n/a", "not available", "null", "none", "nil"].includes(lower) ||
    /^n\s*[\/\-\.]?\s*a\.?$/.test(lower)
  ) {
    return false;
  }
  return true;
};

const parseDate = (dateStr?: string | null): number | null => {
  if (!dateStr || dateStr.trim() === "") return null;
  const cleaned = dateStr.trim();
  const parts = cleaned.split(/[-/]/);
  if (parts.length >= 3) {
    const p0 = parseInt(parts[0], 10);
    const p1 = parseInt(parts[1], 10);
    const p2 = parseInt(parts[2], 10);
    if (!isNaN(p0) && !isNaN(p1) && !isNaN(p2)) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD or YYYY/MM/DD
        return Date.UTC(p0, p1 - 1, p2);
      } else if (parts[2].substring(0, 4).length === 4) {
        // DD-MM-YYYY or DD/MM/YYYY
        const year = parseInt(parts[2].substring(0, 4), 10);
        return Date.UTC(year, p1 - 1, p0);
      }
    }
  }
  const d = new Date(cleaned);
  return isNaN(d.getTime()) ? null : d.getTime();
};

export const isStandardWithdrawn = (item: {
  scStandardAddedDate?: string;
  scStandardRemovedDate?: string;
}): boolean => {
  if (!item.scStandardRemovedDate || item.scStandardRemovedDate.trim() === "") {
    return false;
  }
  if (!item.scStandardAddedDate || item.scStandardAddedDate.trim() === "") {
    return true;
  }
  const added = parseDate(item.scStandardAddedDate);
  const removed = parseDate(item.scStandardRemovedDate);
  if (added === null || removed === null) {
    return true;
  }
  return removed > added;
};

const hasDuplicateValue = <T>(
  rows: T[],
  editingRowIndex: number,
  key: keyof T,
  currentValue: unknown
) =>
  rows.some(
    (row, index) =>
      index !== editingRowIndex &&
      row[key] &&
      String(row[key]) === String(currentValue)
  );

const getValidationErrors = <T>(
  schema: Yup.ObjectSchema<any>,
  values: T
): ValidationErrorMap => {
  try {
    schema.validateSync(values, { abortEarly: false });
    return {};
  } catch (error) {
    if (!(error instanceof Yup.ValidationError)) {
      throw error;
    }

    const fieldErrors: ValidationErrorMap = {};

    if (error.inner.length > 0) {
      error.inner.forEach((validationError) => {
        if (validationError.path && !fieldErrors[validationError.path]) {
          fieldErrors[validationError.path] = validationError.message;
        }
      });
    } else if (error.path) {
      fieldErrors[error.path] = error.message;
    }

    return fieldErrors;
  }
};

const isOtherDescription = (value?: string) =>
  typeof value === "string" && /\bother\b/i.test(value);

export const buildScopeCertificateDetailsValidationSchema = ({
  hasPR0035 = false,
  isFarmSC = false,
  isFarmingAreaOnly = false,
}: {
  hasPR0035?: boolean;
  isFarmSC?: boolean;
  isFarmingAreaOnly?: boolean;
} = {}) =>
  Yup.object().shape({
    scopeCertificateNo: Yup.string()
      .required("Scope Certificate Number is required")
      .length(19, "Must be exactly 19 characters (e.g. SC-########-YYMM-00)")
      .matches(
        /^SC-\d{8}-\d{4}-00$/,
        "Invalid format (XXX-SC-########-YYMM-00)"
      ),
    brandName: Yup.array()
      .of(Yup.string().max(100, "Max 100 characters per brand"))
      .when([], {
        is: () => hasPR0035,
        then: (schema) =>
          schema.min(
            1,
            "Brand Name is required when process code PR0035 is present"
          ),
        otherwise: (schema) => schema.optional(),
      }),
    gotsSCNo: Yup.string().max(100, "Max 100 characters"),
    scLegacyNo: Yup.string()
      .transform((value) => {
        if (typeof value !== "string") return value;
        const trimmed = value.trim();
        if (["na", "NA", "Na", "nA"].includes(trimmed)) {
          return "na";
        }
        return value;
      })
      .required("SC Legacy Number is required")
      .max(100, "Max 100 characters")
      .test(
        "no-whitespace",
        "Whitespace-only value is not allowed in Legacy Number",
        (value) => {
          if (!value) return true;
          return value.trim().length > 0;
        }
      )
      .test(
        "no-notavailable",
        "Variation of Not Available is not allowed",
        (value) => {
          if (!value) return true;
          const cleaned = value.toLowerCase().replace(/\s+/g, "");
          return cleaned !== "notavailable";
        }
      )
      .test(
        "no-none-variant",
        "None variants are not allowed",
        (value) => {
          if (!value) return true;
          const cleaned = value.toLowerCase().replace(/\s+/g, "");
          return cleaned !== "none";
        }
      )
      .test(
        "min-length-or-na",
        'Values with 6 or fewer characters are not allowed (except "na")',
        (value) => {
          if (!value) return true;
          if (value === "na") return true;
          return value.trim().length > 6;
        }
      ),
    scCBAuditorId: Yup.array().of(
      Yup.string()
        .max(10, "Max 10 characters")
        .matches(/^TEU-\d{6}$/, "Format TEU-######")
    ),
    scStandardProgram: Yup.string(),
    isFarmSC: Yup.string()
      .required("Please select whether this Scope Certificate is for a Farm")
      .oneOf(["Y", "N"], "Please select Yes or No"),
    isFarmingAreaOnly: Yup.string().when([], {
      is: () => isFarmSC,
      then: (schema) => schema.required("Please select whether it involves Farming (area) only").oneOf(["Y", "N"], "Please select Yes or No"),
      otherwise: (schema) => schema.optional().nullable(),
    }),
    scNumberOfFarms: Yup.number()
      .nullable()
      .integer("SC Number of Farms must be a whole number")
      .when([], {
        is: () => isFarmSC && !isFarmingAreaOnly,
        then: (schema) =>
          schema
            .required(
              "SC Number of Farms is required when the SC is for a Farm and does not involve Farming (area) only"
            )
            .min(
              1,
              "SC Number of Farms must be at least 1 (cannot be 0 or negative)"
            ),
        otherwise: (schema) =>
          schema.optional().min(0, "SC Number of Farms cannot be negative"),
      }),
    scVersionNo: Yup.string()
      .required("SC Version Number is required")
      .length(19, "Must be exactly 19 characters (e.g. SC-########-YYMM-NN)")
      .matches(
        /^SC-\d{8}-\d{4}-\d{2}$/,
        "Invalid format. Must be of format (XXX-SC-########-YYMM-NN)"
      ),
    scProductLimitations: Yup.string().max(2000, "Max 2000 characters"),
    ccsFlag: Yup.string().required("Choose whether CCS Applies"),
    stdClaimsPolicy: Yup.string().required(),
    certifiedOrganizationApprovedClaims: Yup.string()
      .max(4, "Must be at most 4 characters")
      .oneOf(["PC", "CC", "BOTH", "NONE"], "Invalid value")
      .test(
        "approved-claims-required",
        "Certified Organization Approved Claims is required and cannot be \"NONE\" when process code PR0035 is present",
        function (value) {
          if (hasPR0035 && (!value || value.trim() === "" || value.trim().toUpperCase() === "NONE")) {
            return false;
          }
          return true;
        }
      ),
  });

export const buildCertifiedOrganizationValidationSchema = () =>
  Yup.object().shape({
    certifiedOrganizationTeId: Yup.string()
      .required("TE-ID is required")
      .max(11, "Max 11 characters")
      .matches(/^TE-\d{8}$/, "Format TE-########"),
    certifiedOrganizationName: Yup.string()
      .transform(normalizeNaVariant)
      .required("Name is required")
      .max(255, "Max 255 characters")
      .test("no-whitespace", "Whitespace-only value is not allowed", (value) => {
        if (!value) return true;
        return value.trim().length > 0;
      }),
    certifiedOrganizationCbClientNo: Yup.string()
      .required("Certified Organization CB Client No. is required")
      .max(50, "Max 50 characters")
      .test(
        "no-whitespace",
        "Whitespace-only value is not allowed in CB Client No.",
        (value) => {
          if (!value) return true;
          return value.trim().length > 0;
        }
      )
      .test(
        "no-na-value",
        'CB Client Number cannot be "na" or any NA variation. Please enter a valid client number.',
        (value) => {
          if (!value) return true;
          return !isNaLike(value);
        }
      ),
    certifiedOrganizationNativeName: Yup.string()
      .transform(strictNormalizeNa)
      .required("Certified Organization Native Name is a required field")
      .max(255, "Max 255 characters")
      .test(
        "no-whitespace",
        "Whitespace-only value is not allowed in Native Name",
        (value) => {
          if (!value) return true;
          return value.trim().length > 0;
        }
      )
      .test(
        "no-invalid-na",
        "Combinations of n/a, not available, or null are not allowed.",
        (value) => blockInvalidNaVariants(value)
      )
      .test(
        "min-length-or-na",
        'Values with 6 or fewer characters are not allowed (except "na")',
        (value) => {
          if (!value) return true;
          if (value.trim() === "na") return true;
          return value.trim().length > 6;
        }
      ),
    certifiedOrganizationAddress1: Yup.string()
      .transform(normalizeNaVariant)
      .required("Address is required")
      .max(1000, "Max 1000 characters")
      .test("no-whitespace", "Whitespace-only value is not allowed", (value) => {
        if (!value) return true;
        return value.trim().length > 0;
      }),
    certifiedOrganizationTown: Yup.string()
      .transform(strictNormalizeNa)
      .max(255, "Max 255 characters")
      .test(
        "no-whitespace",
        "Whitespace-only value is not allowed in Town",
        (value) => {
          if (!value) return true;
          return value.trim().length > 0;
        }
      )
      .test(
        "no-invalid-na",
        "Combinations of n/a, not available, or null are not allowed.",
        (value) => blockInvalidNaVariants(value)
      ),
    certifiedOrganizationPostcode: Yup.string()
      .transform(strictNormalizeNa)
      .required()
      .max(50, "Max 50 characters")
      .test(
        "no-whitespace",
        "Whitespace-only value is not allowed in Postcode",
        (value) => {
          if (!value) return true;
          return value.trim().length > 0;
        }
      )
      .test(
        "no-invalid-na",
        "Combinations of n/a, not available, or null are not allowed.",
        (value) => blockInvalidNaVariants(value)
      ),
    certifiedOrganizationCountryOrArea: Yup.string().required(
      "Country/Area is required"
    ),
    certifiedOrganizationStateOrProvince: Yup.string().required(
      "State/Province is required"
    ),
  });

export const buildStandardDetailsValidationSchema = ({
  scStatus,
  scWithdrawalDate,
  scValidFromDate,
  scVersionNo,
}: {
  scStatus?: string;
  scWithdrawalDate?: string;
  scValidFromDate?: string;
  scVersionNo?: string;
}) =>
  Yup.object().shape({
    scStandard: Yup.string().required("SC Standard is required"),
    scStandardDescription: Yup.string(),
    scStandardVersion: Yup.string().required("SC Standard Version is required"),
    scStandardAddedDate: Yup.string()
      .required("SC Standard Added Date is required")
      .test(
        "standard-added-date-sequence",
        "SC Standard Added Date validation failed",
        function (value) {
          if (!value || !scValidFromDate) return true;

          const formatLocalDate = (val: any): string => {
            if (!val) return "";
            if (typeof val === "string") {
              const match = val.match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
              if (match) return `${match[1]}-${match[2]}-${match[3]}`;
            }
            const d = new Date(val);
            if (isNaN(d.getTime())) return "";
            const isUtc =
              d.getUTCHours() === 0 &&
              d.getUTCMinutes() === 0 &&
              d.getUTCSeconds() === 0 &&
              d.getUTCMilliseconds() === 0;
            if (isUtc) {
              const year = d.getUTCFullYear();
              const month = String(d.getUTCMonth() + 1).padStart(2, "0");
              const day = String(d.getUTCDate()).padStart(2, "0");
              return `${year}-${month}-${day}`;
            } else {
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, "0");
              const day = String(d.getDate()).padStart(2, "0");
              return `${year}-${month}-${day}`;
            }
          };

          const addedDateStr = formatLocalDate(value);
          const validFromDateStr = formatLocalDate(scValidFromDate);

          const isInitial = !scVersionNo || scVersionNo.endsWith("00") || !/\d{2}$/.test(scVersionNo);
          if (isInitial) {
            if (addedDateStr !== validFromDateStr) {
              return this.createError({
                message: `SC Standard Added Date must be equal to SC Valid From Date (${validFromDateStr}) for initial version.`,
              });
            }
          } else {
            if (addedDateStr < validFromDateStr) {
              return this.createError({
                message: `SC Standard Added Date cannot be before SC Valid From Date (${validFromDateStr}) for subsequent versions.`,
              });
            }
          }
          return true;
        }
      ),
    scStandardRemovedDate: Yup.string().when([], {
      is: () => scStatus === "W" || !!scWithdrawalDate,
      then: (schema) =>
        schema.required(
          "SC Standard Removed Date is required when SC Status is Withdrawn or Withdrawal Date is filled"
        ),
      otherwise: (schema) => schema.optional(),
    }),
    scStandardWithdrawalReason: Yup.array().when(
      ["scStandardRemovedDate"],
      (scStandardRemovedDate, schema) => {
        if (
          scStatus === "W" ||
          !!scWithdrawalDate ||
          (scStandardRemovedDate && scStandardRemovedDate[0])
        ) {
          return schema.min(1, "SC Standard Withdrawal Reason is required");
        }
        return schema.optional();
      }
    ),
  });

export const buildProductCategoryValidationSchema = () =>
  Yup.object().shape({
    scProductCategoryCode: Yup.string()
      .required("SC Product Category Code is required")
      .length(6, "Must be exactly 6 characters")
      .matches(/^PC\d{4}$/, "Format PC####"),
    scProductCategoryUserSpecificTerm: Yup.string()
      .transform(trimString)
      .max(255, "Max 255 characters")
      .when("scProductCategoryCode", {
        is: "PC0038",
        then: (schema) =>
          schema
            .required(
              "SC Product Category User Specific Term is required for SC Product Category Code PC0038"
            )
            .test(
              "no-whitespace",
              "Whitespace-only value is not allowed",
              (value) => {
          if (!value) return true; // empty/undefined is fine
                return value.trim().length > 0;
              }
            )
            .test(
              "no-invalid-na",
              ({ value }) =>
                `SC Product Category User Specific Term cannot have value ${value}`,
              (value) => !value || Utills.restrictNA(value)
            ),
        otherwise: (schema) => schema.optional(),
      }),
  });

export const buildProductDetailValidationSchema = () =>
  Yup.object().shape({
    scProductDetailCode: Yup.string().required(
      "SC Product Detail Code is required"
    ),
    scProductDetailUserSpecificTerm: Yup.string()
      .transform(trimString)
      
      .when("scProductDetailCode", {
        is: "PD0100",
        then: (schema) =>
          schema.required(
            "SC Product Detail User Specific Term is required for PD0100"
          )
          .test(
            "no-whitespace",
            "Whitespace-only value is not allowed",
            (value) => {
              if (!value) return true;
              return value.trim().length > 0;
            }
          )
          .test(
            "no-invalid-na",
            ({ value }) =>
              `SC Product Detail User Specific Term cannot have value ${value}`,
            (value) => !value || Utills.restrictNA(value)
          ),
        otherwise: (schema) => schema.optional(),
      }),
  });

export const buildRawMaterialValidationSchema = () =>
  Yup.object().shape({
    scProductRawMaterialCode: Yup.string().required(
      "SC Product Raw Material Code is required"
    ),
    scProductRawMaterialUserSpecificTerm: Yup.string()
      .transform(trimString)
      .test(
        "no-whitespace",
        "Whitespace-only value is not allowed",
        (value) => {
          if (!value) return true;
          return value.trim().length > 0;
        }
      )
      .test(
        "no-invalid-na",
        ({ value }) =>
          `SC Product Raw Material User Specific Term cannot have value ${value}`,
        (value) => !value || Utills.restrictNA(value)
      )
      .when("scProductRawMaterialCodeDescription", {
        is: (value: string) => isOtherDescription(value),
        then: (schema) =>
          schema.required(
            "SC Product Raw Material User Specific Term is required for this Raw Material Code"
          ),
        otherwise: (schema) => schema.optional(),
      }),
  });

export const buildFacilityProcessValidationSchema = () =>
  Yup.object().shape({
    facilityProcessCategoryCode: Yup.string().required(
      "Process Category Code is required"
    ),
    facilityProcessCategoryUserSpecificTerm: Yup.string()
      .transform(trimString)
      .test(
        "no-whitespace",
        "Whitespace-only value is not allowed",
        (value) => {
          if (!value) return true;
          return value.trim().length > 0;
        }
      )
      .test(
        "no-na",
        'Facility Process Category User Specific Term cannot have value "na"',
        (value) => !value || Utills.restrictNA(value)
      )
      .when("facilityProcessCategoryCode", {
        is: "PR0034",
        then: (schema) =>
          schema.required("User Specific Term is required for PR0034"),
        otherwise: (schema) => schema.optional(),
      }),
  });

export const validateProcessCodesAgainstStandards = (
  mergedCodes: string[],
  {
    scNoOfFarmsFlag,
    activeStandards,
    isFarmingAreaOnly = false,
  }: {
    scNoOfFarmsFlag: boolean;
    activeStandards: string[];
    isFarmingAreaOnly?: boolean;
  }
): string | null => {
  if (!scNoOfFarmsFlag) {
    const invalidCodes = ["PR0011", "PR0036", "PR0037", "PR0038"];
    const foundInvalid = mergedCodes.filter((code) => invalidCodes.includes(code));
    if (foundInvalid.length > 0) {
      return "Facility process code (PR code) cannot be PR0011, PR0036, PR0037 and PR0038 when the Scope Certificate is not for a Farm.";
    }
    return null;
  }

  // If Farming (area) only is selected (isFarmingAreaOnly === true), process codes PR0011, PR0036, PR0038 are not allowed.
  if (isFarmingAreaOnly) {
    const invalidCodes = ["PR0011", "PR0036", "PR0038"];
    const foundInvalid = mergedCodes.filter((code) => invalidCodes.includes(code));
    if (foundInvalid.length > 0) {
      return `Farming (area) only is selected — process codes ${foundInvalid.join(", ")} are not allowed. Number of farms is required for these process codes.`;
    }
  }

  const farmProcessCodes = ["PR0011", "PR0036", "PR0037", "PR0038"];
  const hasFarmProcessCode = mergedCodes.some((code) => farmProcessCodes.includes(code));
  if (!hasFarmProcessCode) {
    return null;
  }

  if (activeStandards.some((standard) => ["RWS", "RMS", "RAS"].includes(standard))) {
    if (!mergedCodes.some((code) => ["PR0011", "PR0036", "PR0038"].includes(code))) {
      return "For RAF standards (RWS/RMS/RAS), at least one of PR0011, PR0036, or PR0038 must be selected.";
    }
  }

  if (activeStandards.includes("RDS")) {
    if (!mergedCodes.some((code) => ["PR0011", "PR0036", "PR0037"].includes(code))) {
      return "For RDS standard, at least one of PR0011, PR0036, or PR0037 must be selected.";
    }
  }

  if (activeStandards.includes("MMS")) {
    if (mergedCodes.includes("PR0037")) {
      return "For MMS standard, PR0037 is not allowed.";
    }
    if (!mergedCodes.some((code) => ["PR0011", "PR0036", "PR0038"].includes(code))) {
      return "For MMS standard, at least one of PR0011, PR0036, or PR0038 must be selected.";
    }
  }

  const hasRequiredStandard = activeStandards.some((std) => ["RAS", "RMS", "RWS", "RDS", "MMS"].includes(std));
  if (!hasRequiredStandard) {
    return "If a farm process code (PR0011, PR0036, PR0037 or PR0038) is recorded, the facility standard must include at least RAS, RMS, RWS, RDS, or MMS.";
  }

  return null;
};

export const validateFarmCapacityRawMaterials = (
  rmCodes: string[],
  activeStandards: string[],
  processCodes: string[]
): string | null => {
  const farmProcessCodes = ["PR0011", "PR0036", "PR0037", "PR0038"];
  const hasFarmProcessCode = processCodes.some((code) => farmProcessCodes.includes(code));
  if (!hasFarmProcessCode) {
    return null;
  }

  if (activeStandards.includes("RAS")) {
    if (!rmCodes.includes("RM0007")) {
      return "When facility standard is RAS, the facility farm raw material code must include at least RM0007.";
    }
  }

  if (activeStandards.includes("RMS")) {
    if (!rmCodes.includes("RM0064")) {
      return "When facility standard is RMS, the facility farm raw material code must include at least RM0064.";
    }
  }

  if (activeStandards.includes("RWS")) {
    if (!rmCodes.includes("RM0083")) {
      return "When facility standard is RWS, the facility farm raw material code must include at least RM0083.";
    }
  }

  if (activeStandards.includes("RDS")) {
    const requiredRDS = ["RM0032", "RM0039", "RM0330"];
    const hasRDS = rmCodes.some((code) => requiredRDS.includes(code));
    if (!hasRDS) {
      return "When facility standard is RDS, the facility farm raw material code must include at least RM0032, RM0039, or RM0330.";
    }
  }

  if (activeStandards.includes("MMS")) {
    const requiredMMS = ["RM0007", "RM0064", "RM0083"];
    const hasMMS = rmCodes.some((code) => requiredMMS.includes(code));
    if (!hasMMS) {
      return "When facility standard is MMS, the facility farm raw material code must include at least RM0007, RM0064, or RM0083.";
    }
  }

  return null;
};

export const validateProductCategoryRow = (
  row: IProductCategoryInformation,
  rows: IProductCategoryInformation[] = [],
  editingRowIndex = -1
): RowValidationResult => {
  const fieldErrors = getValidationErrors(
    buildProductCategoryValidationSchema(),
    row
  );

  if (
    !fieldErrors.scProductCategoryCode &&
    row.scProductCategoryCode &&
    hasDuplicateValue(
      rows,
      editingRowIndex,
      "scProductCategoryCode",
      row.scProductCategoryCode
    )
  ) {
    return {
      fieldErrors,
      generalError: "SC Product Category Code must be unique across all rows",
    };
  }

  return { fieldErrors };
};

export const validateProductDetailRow = (
  row: IProductDetailInformation,
  rows: IProductDetailInformation[] = [],
  editingRowIndex = -1
): RowValidationResult => {
  const fieldErrors = getValidationErrors(
    buildProductDetailValidationSchema(),
    row
  );

  if (
    !fieldErrors.scProductDetailCode &&
    row.scProductDetailCode &&
    hasDuplicateValue(
      rows,
      editingRowIndex,
      "scProductDetailCode",
      row.scProductDetailCode
    )
  ) {
    return {
      fieldErrors,
      generalError: "SC Product Detail Code must be unique across all rows",
    };
  }

  return { fieldErrors };
};

export const validateRawMaterialRow = (
  row: IRawMaterialDetails,
  rows: IRawMaterialDetails[] = [],
  editingRowIndex = -1
): RowValidationResult => {
  const fieldErrors = getValidationErrors(buildRawMaterialValidationSchema(), row);

  if (
    !fieldErrors.scProductRawMaterialCode &&
    row.scProductRawMaterialCode &&
    hasDuplicateValue(
      rows,
      editingRowIndex,
      "scProductRawMaterialCode",
      row.scProductRawMaterialCode
    )
  ) {
    return {
      fieldErrors,
      generalError: "SC Product Raw Material Code must be unique across all rows",
    };
  }

  return { fieldErrors };
};

/**
 * Returns true when scNumberOfFarms is applicable.
 * PR0011 / PR0036 / PR0038  → Number of Farms IS required (counting individual farm sites).
 * PR0037 (Farming - area)   → Number of Farms is NOT applicable; it uses farm area instead.
 *
 * Logic: at least one of PR0011/PR0036/PR0038 must be present for the field to be shown/required.
 */
export const isFarmNumberRequired = (processCodes: string[]): boolean => {
  const farmCountCodes = ["PR0011", "PR0036", "PR0038"];
  return processCodes.some((code) => farmCountCodes.includes(code));
};

export const validateFacilityProcessRow = (
  row: FacilityProcessDetails,
  {
    rows = [],
    editingRowIndex = -1,
    scNoOfFarmsFlag: _scNoOfFarmsFlag = false,
    activeStandards = [],
  }: {
    rows?: FacilityProcessDetails[];
    editingRowIndex?: number;
    scNoOfFarmsFlag?: boolean;
    activeStandards?: string[];
  } = {}
): RowValidationResult => {
  const fieldErrors = getValidationErrors(
    buildFacilityProcessValidationSchema(),
    row
  );

  // ── PR0037 is only applicable for RDS ────────────────────────────────────
  // Block at row-save level so the user sees an immediate, actionable error.
  if (
    !fieldErrors.facilityProcessCategoryCode &&
    row.facilityProcessCategoryCode === "PR0037"
  ) {
    if (!activeStandards.includes("RDS")) {
      return {
        fieldErrors,
        generalError:
          "PR0037 (Farming - area / Farm production for a farm area) is only applicable for RDS standard. " +
          "Please add RDS to the facility standards before selecting PR0037.",
      };
    }
  }

  if (
    !fieldErrors.facilityProcessCategoryCode &&
    row.facilityProcessCategoryCode &&
    hasDuplicateValue(
      rows,
      editingRowIndex,
      "facilityProcessCategoryCode",
      row.facilityProcessCategoryCode
    )
  ) {
    return {
      fieldErrors,
      generalError:
        "Facility Process Category Code must be unique across all rows",
    };
  }

  return { fieldErrors };
};

export const checkYymmConsistency = (
  scNo?: string,
  validFromDate?: string | number | null
): string | null => {
  if (!scNo || !validFromDate) return null;

  const match = scNo.match(/^SC-\d{8}-(\d{2})(\d{2})-\d{2}$/);
  if (!match) return null;

  const scYY = match[1];
  const scMM = match[2];
  const fromDate = new Date(String(validFromDate));

  if (isNaN(fromDate.getTime())) return null;

  const fromYY = String(fromDate.getFullYear()).slice(-2);
  const fromMM = String(fromDate.getMonth() + 1).padStart(2, "0");

  if (scYY !== fromYY || scMM !== fromMM) {
    return `YYMM in SC Number (${scYY}${scMM}) must match the Year and Month of SC Valid From Date (${fromYY}${fromMM}). Please go back to Step 1 and update the SC Number.`;
  }

  return null;
};

export type ScopeCertificateDetailsShape = ScopeCertificateDetails;
export type CertifiedOrganizationDetailsShape = ICertifiedOrganizationDetails;
export type StandardDetailsShape = StandardDetails;
